import { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from '../Dialog'
import { FilterChips, type FilterChip } from '../FilterChips'
import { Picker } from '../Picker'
import { OneMarkIcon, SearchIcon } from '../icons'
import { initialsOf } from '../../lib/initials'
import { messageOf } from '../../lib/errors'
import type { OrgUnitOption } from '../../lib/admin'
import { ASSIGNABLE_ROLES, catalogueEntry, scopeOptionsFor, type PeopleScope } from '../../lib/roles'
import {
  scopeIds,
  scopeOptions,
  scopeWords,
  whereLabel,
  whereOptions,
  whereOrgUnitIds,
  type BuPlace,
  type WhereTree,
} from '../../lib/where'
import {
  DECLINE_REASONS,
  acceptSuggestion,
  answerSuggestions,
  declineSuggestion,
  holderRoleAllowed,
  laterSuggestion,
  loadAnswers,
  loadGrantSuggestions,
  loadRulings,
  loadSetup,
  machineLine,
  matchLine,
  reasonLabel,
  sortNewTitles,
  suggestionAction,
  suggestionToken,
  summariseBulk,
  waitingWords,
  type BulkAnswer,
  type BulkResult,
  type DeclineReason,
  type GrantSuggestion,
  type Ruling,
  type SuggestionAnswer,
  type SuggestionSetup,
} from '../../lib/roleSuggestions'
import { JobTitleSorts } from './JobTitleSorts'
import { ScopePicker } from './ScopePicker'
import { TypicalHolders } from './TypicalHolders'

/**
 * The Suggestions tab on People and roles — Who Holds What, B1 (154).
 *
 * ── DERIVED WHEN THE PAGE IS OPENED ─────────────────────────────────────────
 *
 * Nothing here was saved before somebody answered it. role_suggestions asks
 * the confirmed typical holders and the confirmed title sorts what they say
 * now, minus what was answered with the same evidence and what was put off.
 * So the tab cannot show a stale suggestion, and reloading is the refresh.
 *
 * ── THE TITLE SORT RUNS WHEN THIS TAB OPENS ─────────────────────────────────
 *
 * For a group administrator, if titles exist that nobody has sorted, opening
 * the tab asks OneConetec to sort them (the role-titles Edge Function, titles
 * only, as the caller). That is the whole trigger — no cron (rule 2). What it
 * sorts is only a SUGGESTION until HR accepts it in Job titles, and nothing
 * matches on a suggestion.
 *
 * ── TEAL MARKS ONLY THE MACHINE'S PART ──────────────────────────────────────
 *
 * The suggestion itself comes from a typical holder an administrator wrote and
 * confirmed: not teal. The one thing a machine decided is a title's function,
 * and only a suggestion that matched through a sort OneConetec made carries the
 * teal line and the mark (the AI disclosure, hub.css).
 *
 * ── FILTERS, SEVERAL AT ONCE, AND A SCOPE THAT CAN CHANGE (159) ────────────
 *
 * David, 11 Oct. The People tab's filters (Where, Department, Job title, Role
 * — here the SUGGESTED role) and the name search, sent to the database: the
 * list is limited there, so filtering here would be some of the matches
 * presented as all of them (standing rule 1). Rows can be selected and
 * answered together; each answer still goes through the single-answer
 * function, and a miss comes back by name. A scopable role's scope can be
 * changed on the row before accepting — Dustin's "Canada and BU 51" — and the
 * picker shows which grants that makes before anything is granted.
 *
 * ── WHAT IT WILL NOT DO ─────────────────────────────────────────────────────
 *
 * Grant anything by itself (Accept is assign_role, as the panel). Count or
 * rank who answered (B22): answers are listed for the audit trail, never
 * totalled per person. Send a typed reason anywhere: it stays in the answer
 * log, readable only by administrators who could grant to that person.
 */
export function RoleSuggestions({
  orgUnits,
  places = [],
  tree = [],
  scope = null,
  titles = [],
  canPrivileged,
  onGranted,
  initial,
}: {
  orgUnits: OrgUnitOption[]
  /** 159. Each business unit's country: what turns "Canada" into its entities. */
  places?: BuPlace[]
  /** 159. The Where tree the People tab filters with (a regional holder's region). */
  tree?: WhereTree
  /** 153. Whose page this is: a regional holder's scope picker offers their region only. */
  scope?: PeopleScope | null
  /** 159. The job titles the filter offers (people_roster_titles). */
  titles?: { title: string; people: number }[]
  /** admin.roles.privileged — may write who Tenant and HR Administrator are for. */
  canPrivileged: boolean
  /** A grant was made: the roster behind the other tab is stale. */
  onGranted?: () => void
  /** The render suite's way in: effects do not run under renderToString. */
  initial?: {
    page: { suggestions: GrantSuggestion[]; total: number }
    setup: SuggestionSetup
    rulings?: Ruling[]
    answers?: SuggestionAnswer[]
  }
}) {
  const [page, setPage] = useState(initial?.page ?? null)
  const [setup, setSetup] = useState<SuggestionSetup | null>(initial?.setup ?? null)
  const [rulings, setRulings] = useState<Ruling[]>(initial?.rulings ?? [])
  const [answers, setAnswers] = useState<SuggestionAnswer[]>(initial?.answers ?? [])
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)
  const [misses, setMisses] = useState<BulkResult[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [declining, setDeclining] = useState<GrantSuggestion | null>(null)
  const [decliningMany, setDecliningMany] = useState<GrantSuggestion[] | null>(null)
  const [sorting, setSorting] = useState<number | null>(null)
  const [tray, setTray] = useState<'titles' | 'holders' | null>(null)
  const sortedOnce = useRef(false)

  // ── 159: the filters, in the database ────────────────────────────────────
  const [search, setSearch] = useState('')
  const [whereFilter, setWhereFilter] = useState<string[]>([])
  const [deptFilter, setDeptFilter] = useState<string[]>([])
  const [titleFilter, setTitleFilter] = useState<string[]>([])
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const whereIds = useMemo(() => whereOrgUnitIds(whereFilter, tree), [whereFilter, tree])
  const filterKey = JSON.stringify([search.trim(), whereIds, deptFilter, titleFilter, roleFilter])
  const latestPage = useRef(0)

  // ── 159: selection, and scopes chosen on a row ────────────────────────────
  const [checked, setChecked] = useState<Set<string>>(new Set())
  /** token → the picker's choices and the org units they grant for. */
  const [scopes, setScopes] = useState<Record<string, { values: string[]; ids: string[]; words: string }>>({})
  const [scoping, setScoping] = useState<GrantSuggestion | null>(null)
  const [scopeDraft, setScopeDraft] = useState<string[]>([])

  async function reloadPage() {
    const ticket = ++latestPage.current
    try {
      const p = await loadGrantSuggestions(200, {
        search,
        whereIds,
        departmentIds: deptFilter,
        jobTitles: titleFilter,
        roleKeys: roleFilter,
      })
      // A response to an older filter is dropped, error included.
      if (ticket !== latestPage.current) return
      setPage(p)
      const shown = new Set(p.suggestions.map(suggestionToken))
      setChecked((prev) => new Set([...prev].filter((t) => shown.has(t))))
      setError(null)
    } catch (e) {
      if (ticket !== latestPage.current) return
      setError(messageOf(e))
    }
  }

  async function reloadSide() {
    try {
      const [s, r, a] = await Promise.all([loadSetup(), loadRulings(), loadAnswers(50)])
      setSetup(s)
      setRulings(r)
      setAnswers(a)
      return s
    } catch (e) {
      setError(messageOf(e))
      return null
    }
  }

  async function reload() {
    await Promise.all([reloadPage(), reloadSide()])
  }

  useEffect(() => {
    if (initial) return
    let live = true
    void (async () => {
      const s = await reloadSide()
      // Once per visit: the tab opening is the trigger (rule 2).
      if (!live || !s || !s.maySetUp || s.unsorted === 0 || sortedOnce.current) return
      sortedOnce.current = true
      setSorting(s.unsorted)
      const r = await sortNewTitles()
      if (!live) return
      setSorting(null)
      if (r.error) setError(`OneConetec could not sort the new job titles: ${r.error}`)
      else if (r.sorted > 0) {
        setOutcome(`OneConetec sorted ${r.sorted} new job title${r.sorted === 1 ? '' : 's'}. They match nothing until HR accepts them in Job titles.`)
        await reload()
      }
    })()
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The list: on opening, and 250 ms after a filter last changed, so typing a
  // name is not a request per key.
  useEffect(() => {
    if (initial) return
    const t = setTimeout(() => void reloadPage(), 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey])

  async function act(token: string, fn: () => Promise<string | void>, done: (r: string | void) => string) {
    setBusy(token)
    setError(null)
    setMisses([])
    try {
      const r = await fn()
      setOutcome(done(r))
      await reload()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const forgetScope = (token: string) =>
    setScopes((prev) => {
      const next = { ...prev }
      delete next[token]
      return next
    })

  const accept = (s: GrantSuggestion) => {
    const token = suggestionToken(s)
    const chosen = scopes[token]
    return act(`accept:${token}`, () => acceptSuggestion(s, chosen?.ids ?? null), () => {
      onGranted?.()
      forgetScope(token)
      return `${chosen ? `Add ${s.roleName} for ${chosen.words}` : suggestionAction(s)} — granted to ${s.displayName}. Recorded as a correct suggestion.`
    })
  }

  const later = (s: GrantSuggestion) =>
    act(`later:${suggestionToken(s)}`, () => laterSuggestion(s), (until) =>
      `${s.displayName}: hidden until ${until ? new Date(until).toLocaleDateString() : 'next month'}. Nothing was recorded.`)

  const suggestions = page?.suggestions ?? []
  const total = page?.total ?? 0
  const selected = suggestions.filter((s) => checked.has(suggestionToken(s)))

  async function answerMany(answer: BulkAnswer, reasons: DeclineReason[] = [], note = '') {
    const items = selected
    if (items.length === 0) return
    setBusy(`bulk:${answer}`)
    setError(null)
    setMisses([])
    try {
      const results = await answerSuggestions(
        answer,
        items,
        Object.fromEntries(Object.entries(scopes).map(([t, v]) => [t, v.ids])),
        reasons,
        note,
      )
      setOutcome(summariseBulk(answer, results))
      setMisses(results.filter((r) => !r.done))
      const doneTokens = new Set(results.filter((r) => r.done).map(suggestionToken))
      setChecked(new Set())
      setScopes((prev) => Object.fromEntries(Object.entries(prev).filter(([t]) => !doneTokens.has(t))))
      if (answer === 'accept' && doneTokens.size > 0) onGranted?.()
      await reload()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  // ── the scope dialog ──────────────────────────────────────────────────────
  const scopingRole = scoping ? catalogueEntry(scoping.roleKey) : undefined
  const scopingUnits = useMemo(
    () => (scopingRole ? scopeOptionsFor(scopingRole, orgUnits, scope) : []),
    [scopingRole, orgUnits, scope],
  )
  function openScope(s: GrantSuggestion) {
    setScopeDraft(scopes[suggestionToken(s)]?.values ?? (s.orgUnitId ? [s.orgUnitId] : []))
    setScoping(s)
  }
  function saveScope() {
    if (!scoping) return
    const token = suggestionToken(scoping)
    const { options, tree: scopeTree } = scopeOptions(scopingUnits, places)
    const ids = scopeIds(scopeDraft, scopeTree)
    // Back to the suggestion's own scope, or nothing chosen: no change.
    if (ids.length === 0 || (ids.length === 1 && ids[0] === scoping.orgUnitId)) forgetScope(token)
    else setScopes((prev) => ({ ...prev, [token]: { values: scopeDraft, ids, words: scopeWords(scopeDraft, options) } }))
    setScoping(null)
  }

  // ── filter options and chips (the People tab's, 137 and 159) ─────────────
  const where = whereOptions(tree)
  const departments = orgUnits.filter((o) => o.type === 'department')
  const roleOptions = ASSIGNABLE_ROLES.filter((r) => holderRoleAllowed(r.key) && (canPrivileged || !r.privileged)).map((r) => ({
    value: r.key,
    label: r.name,
  }))
  type Group = 'Where' | 'Department' | 'Job title' | 'Role'
  const chips: FilterChip[] = [
    ...whereFilter.map((v) => ({ group: 'Where', value: v, label: whereLabel(v, where) })),
    ...deptFilter.map((id) => ({ group: 'Department', value: id, label: orgUnits.find((u) => u.orgUnitId === id)?.name ?? id })),
    ...titleFilter.map((t) => ({ group: 'Job title', value: t, label: t })),
    ...roleFilter.map((k) => ({ group: 'Role', value: k, label: roleOptions.find((r) => r.value === k)?.label ?? k })),
  ]
  const setters: Record<Group, (fn: (v: string[]) => string[]) => void> = {
    Where: setWhereFilter,
    Department: setDeptFilter,
    'Job title': setTitleFilter,
    Role: setRoleFilter,
  }
  function clearFilters() {
    setWhereFilter([])
    setDeptFilter([])
    setTitleFilter([])
    setRoleFilter([])
    setSearch('')
  }
  const filtered = chips.length > 0 || search.trim() !== ''
  const allShownChecked = suggestions.length > 0 && selected.length === suggestions.length
  const changedInSelection = selected.filter((s) => scopes[suggestionToken(s)]).length

  return (
    <div className="suggest">
      <div className="people__bar">
        <p className="people__count" aria-live="polite">
          {page === null ? (
            'Looking for suggestions…'
          ) : (
            <>
              <strong>{total}</strong> grant suggestion{total === 1 ? '' : 's'}
              {filtered && <span className="people__countnote"> match</span>}
              {total > suggestions.length && (
                <span className="people__countnote"> · first {suggestions.length} shown</span>
              )}
            </>
          )}
        </p>
        {setup?.maySetUp && (
          <div className="people__actions">
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setTray('titles')}>
              Job titles
              {setup.toCheck + setup.functionsDraft > 0 && (
                <span className="suggest__count"> · {setup.toCheck + setup.functionsDraft} to check</span>
              )}
            </button>
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setTray('holders')}>
              Typical holders
              {setup.holdersDraft > 0 && <span className="suggest__count"> · {setup.holdersDraft} drafts</span>}
            </button>
          </div>
        )}
      </div>
      {/* 303. A count with no cause is what David read on 22 Oct: say which functions a confirmed holder
          names whose sorts nobody has accepted, because those holders match nobody through them. */}
      {setup?.maySetUp && setup.waiting.some((w) => w.holders.length > 0) && (
        <div className="suggest__waiting" role="status">
          {setup.waiting
            .filter((w) => w.holders.length > 0)
            .map((w) => (
              <p key={w.functionKey} className="admin__muted">
                {waitingWords(w)} Named by {w.holders.join(', ')}.
              </p>
            ))}
        </div>
      )}

      {/* 159. The People tab's filter row, sent to the database. */}
      <div className="people__filters" role="search" aria-label="Find suggestions">
        <label className="people__search">
          <span className="searchwrap__icon" aria-hidden="true">
            <SearchIcon size={15} />
          </span>
          <span className="visually-hidden">Find someone by name</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find someone"
            autoComplete="off"
          />
        </label>
        <Picker
          variant="pill"
          className="picker--where"
          label="Where"
          searchPlaceholder="Country, entity or business unit"
          options={where}
          selected={whereFilter}
          onChange={setWhereFilter}
        />
        <Picker
          variant="pill"
          label="Department"
          searchPlaceholder="Department"
          options={departments.map((o) => ({ value: o.orgUnitId, label: o.name }))}
          selected={deptFilter}
          onChange={setDeptFilter}
        />
        <Picker
          variant="pill"
          label="Job title"
          searchPlaceholder="Job title"
          options={titles.map((t) => ({ value: t.title, label: t.title, count: t.people }))}
          selected={titleFilter}
          onChange={setTitleFilter}
        />
        <Picker
          variant="pill"
          label="Role"
          searchPlaceholder="Suggested role"
          options={roleOptions}
          selected={roleFilter}
          onChange={setRoleFilter}
        />
      </div>

      <FilterChips
        chips={chips}
        onRemove={(c) => setters[c.group as Group]((v) => v.filter((x) => x !== c.value))}
        onClear={clearFilters}
      />

      {sorting !== null && (
        <p className="suggest__sorting" role="status">
          <OneMarkIcon size={14} /> OneConetec is sorting {sorting} new job title{sorting === 1 ? '' : 's'} into
          functions. Titles only — no names.
        </p>
      )}

      {outcome && (
        <div className="people__outcomewrap" role="status">
          <p className="people__outcome">
            {outcome}
            <button
              type="button"
              className="chip__x"
              aria-label="Dismiss"
              onClick={() => {
                setOutcome(null)
                setMisses([])
              }}
            >
              ×
            </button>
          </p>
          {misses.length > 0 && (
            <details className="people__misses" open>
              <summary>Not done ({misses.length})</summary>
              <ul>
                {misses.map((m) => (
                  <li key={suggestionToken(m)}>
                    <strong>{m.displayName}</strong> — {m.why}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      <div className="people__tablecard">
        {page && suggestions.length === 0 && (
          <div className="people__empty">
            <p className="empty">{filtered ? 'No suggestions match those filters.' : emptyLine(setup)}</p>
            {filtered && (
              <button type="button" className="btn btn--quiet btn--sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        )}
        {suggestions.length > 0 && (
          <>
            <div className="suggest__selectall">
              <button
                type="button"
                className="admin__pick"
                aria-pressed={allShownChecked}
                aria-label={allShownChecked ? 'Clear the selection' : `Select all ${suggestions.length} shown`}
                onClick={() =>
                  setChecked(allShownChecked ? new Set() : new Set(suggestions.map(suggestionToken)))
                }
              >
                ✓
              </button>
              <span className="admin__muted">
                {allShownChecked ? `All ${suggestions.length} shown are selected` : `Select all ${suggestions.length} shown`}
              </span>
            </div>
            <SuggestionList
              suggestions={suggestions}
              busy={busy}
              onAccept={accept}
              onDecline={setDeclining}
              onLater={later}
              checked={checked}
              onToggle={(tokens, on) =>
                setChecked((prev) => {
                  const next = new Set(prev)
                  for (const t of tokens) {
                    if (on) next.add(t)
                    else next.delete(t)
                  }
                  return next
                })
              }
              scopes={scopes}
              onScope={openScope}
            />
          </>
        )}
      </div>

      {/* 159. Under the list and sticky to the bottom edge, as on the People tab. */}
      {selected.length > 0 && (
        <div className="bulkbar" role="region" aria-label="Answer the selected suggestions">
          <p className="bulkbar__count">
            <strong>{selected.length}</strong> selected
            {changedInSelection > 0 && (
              <span className="admin__muted"> · {changedInSelection} with a changed scope</span>
            )}
          </p>
          <div className="bulkbar__group">
            <button type="button" className="btn btn--primary btn--sm" disabled={busy !== null} onClick={() => void answerMany('accept')}>
              {busy === 'bulk:accept' ? 'Accepting…' : `Accept ${selected.length}`}
            </button>
            <button type="button" className="btn btn--sm" disabled={busy !== null} onClick={() => setDecliningMany(selected)}>
              {busy === 'bulk:decline' ? 'Declining…' : `Decline ${selected.length}…`}
            </button>
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              disabled={busy !== null}
              title="Hide these for 30 days. Not a decline: nothing is recorded."
              onClick={() => void answerMany('later')}
            >
              {busy === 'bulk:later' ? '…' : 'Later'}
            </button>
            <button type="button" className="btn btn--quiet btn--sm" disabled={busy !== null} onClick={() => setChecked(new Set())}>
              Clear
            </button>
          </div>
        </div>
      )}

      {page && (
        <p className="suggest__foot">
          Suggestions come from typical holders an administrator confirmed. Accepting grants the role exactly as the
          person’s panel does; declining grants nothing. Every answer is kept, and the next suggestions learn from them.
        </p>
      )}

      {rulings.length > 0 && (
        <details className="people__misses suggest__rulings">
          <summary>Settled, so never suggested ({rulings.length})</summary>
          <ul>
            {rulings.map((r) => (
              <li key={`${r.roleKey}:${r.kind}`}>
                <strong>{r.roleName}</strong> · {r.ref} — {r.words}
              </li>
            ))}
          </ul>
        </details>
      )}

      {answers.length > 0 && (
        <AnswerList
          answers={answers}
          busy={busy}
          onAcceptInstead={(a) =>
            act(`accept:${a.personId}:${a.holderId}`, () =>
              acceptSuggestion({ personId: a.personId, holderId: a.holderId, evidenceMd5: a.currentEvidenceMd5 ?? '' }),
            () => {
              onGranted?.()
              return `${a.roleName} granted to ${a.displayName}. Recorded as a new answer; the decline stays in the record.`
            })
          }
        />
      )}

      <DeclineDialog
        suggestion={declining}
        many={decliningMany}
        onCancel={() => {
          setDeclining(null)
          setDecliningMany(null)
        }}
        onDecline={async (reasons, note) => {
          if (decliningMany) {
            setDecliningMany(null)
            await answerMany('decline', reasons, note)
            return
          }
          const s = declining!
          setDeclining(null)
          await act(`decline:${suggestionToken(s)}`, () => declineSuggestion(s, reasons, note), () =>
            `${suggestionAction(s)} for ${s.displayName} — declined. Nothing was granted.`)
        }}
      />

      <Dialog
        open={scoping !== null}
        title={scoping ? `Where ${scoping.displayName} is ${scoping.roleName}` : 'Scope'}
        onClose={() => setScoping(null)}
      >
        {scoping && (
          <form
            className="suggest__scope"
            onSubmit={(e) => {
              e.preventDefault()
              saveScope()
            }}
          >
            <p className="admin__muted">
              Suggested for {scoping.orgUnitName ?? 'the whole company'}
              {scoping.orgUnitName && scoping.legalEntityName === scoping.orgUnitName ? ', their own legal entity' : ''}.
              Choose every part of the company it should cover — a country, legal entities or business units. Nothing is
              granted until you accept.
            </p>
            <ScopePicker
              roleName={scoping.roleName}
              units={scopingUnits}
              places={places}
              selected={scopeDraft}
              onChange={setScopeDraft}
            />
            <div className="admin__inviteactions">
              <button type="submit" className="btn btn--primary btn--sm" disabled={scopeDraft.length === 0}>
                Use this scope
              </button>
              {scoping.orgUnitId && (
                <button
                  type="button"
                  className="btn btn--quiet btn--sm"
                  onClick={() => setScopeDraft([scoping.orgUnitId!])}
                >
                  Back to {scoping.orgUnitName}
                </button>
              )}
              <button type="button" className="btn btn--quiet" onClick={() => setScoping(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </Dialog>

      <Dialog open={tray === 'titles'} title="Job titles and functions" variant="tray" className="people__tray" onClose={() => { setTray(null); void reload() }}>
        {tray === 'titles' && <JobTitleSorts />}
      </Dialog>
      <Dialog open={tray === 'holders'} title="Typical holders" variant="tray" className="people__tray" onClose={() => { setTray(null); void reload() }}>
        {tray === 'holders' && <TypicalHolders orgUnits={orgUnits} canPrivileged={canPrivileged} />}
      </Dialog>
    </div>
  )
}

/** Why the list is empty, in the words that say what to do about it. */
export function emptyLine(setup: SuggestionSetup | null): string {
  if (setup?.maySetUp && setup.holdersDraft > 0)
    return `No suggestions yet. ${setup.holdersDraft} typical holder${setup.holdersDraft === 1 ? ' is a draft' : 's are drafts'}, and a draft suggests nobody until an administrator confirms it in Typical holders.`
  return 'No suggestions right now. Everyone who looks like a typical holder either holds the role or has been answered.'
}

/**
 * The rows, grouped under the typical holder that made them, in the server's
 * order (no ranking: B2). The holder's reason is printed once above its people.
 */
export function SuggestionList({
  suggestions,
  busy,
  onAccept,
  onDecline,
  onLater,
  checked,
  onToggle,
  scopes = {},
  onScope,
}: {
  suggestions: GrantSuggestion[]
  busy: string | null
  onAccept: (s: GrantSuggestion) => void
  onDecline: (s: GrantSuggestion) => void
  onLater: (s: GrantSuggestion) => void
  /** 159. Which rows are selected. Absent, the rows carry no selector. */
  checked?: Set<string>
  /** 159. Select or clear these rows (one row, or a whole group). */
  onToggle?: (tokens: string[], on: boolean) => void
  /** 159. Scopes chosen on a row instead of the suggested one. */
  scopes?: Record<string, { words: string }>
  /** 159. Change the scope of a row whose role can be scoped. Absent, no control. */
  onScope?: (s: GrantSuggestion) => void
}) {
  const groups: { key: string; head: GrantSuggestion; rows: GrantSuggestion[] }[] = []
  for (const s of suggestions) {
    const key = `${s.holderId}:${s.roleKey}`
    const g = groups.find((x) => x.key === key)
    if (g) g.rows.push(s)
    else groups.push({ key, head: s, rows: [s] })
  }
  return (
    <div className="suggest__groups">
      {groups.map((g) => (
        <section key={g.key} className="suggest__group" aria-label={`${g.head.holderName}: ${g.head.roleName}`}>
          <header className={`suggest__ghead${checked && onToggle ? ' suggest__ghead--pick' : ''}`}>
            {checked && onToggle && (() => {
              const tokens = g.rows.map(suggestionToken)
              const all = tokens.every((t) => checked.has(t))
              return (
                <button
                  type="button"
                  className="admin__pick"
                  aria-pressed={all}
                  aria-label={all ? `Clear ${g.head.holderName}` : `Select everyone under ${g.head.holderName}`}
                  onClick={() => onToggle(tokens, !all)}
                >
                  ✓
                </button>
              )
            })()}
            <h3 className="suggest__gname">
              {g.head.holderName}
              <span className="suggest__arrow" aria-hidden="true"> → </span>
              <span className="rolechip">{g.head.roleName}</span>
            </h3>
            <p className="suggest__why">{g.head.holderWhy}</p>
          </header>
          <ul className="suggest__list">
            {g.rows.map((s) => {
              const token = suggestionToken(s)
              const working = busy !== null
              const machine = machineLine(s)
              const plain = machine ? null : matchLine(s)
              const chosen = scopes[token]
              const scopable = !!onScope && !!catalogueEntry(s.roleKey)?.scopable
              return (
                <li key={token} className={`suggest__row${checked && onToggle ? ' suggest__row--pick' : ''}`}>
                  {checked && onToggle && (
                    <button
                      type="button"
                      className="admin__pick"
                      aria-pressed={checked.has(token)}
                      aria-label={`Select ${s.displayName}`}
                      onClick={() => onToggle([token], !checked.has(token))}
                    >
                      ✓
                    </button>
                  )}
                  <span className="pavatar" aria-hidden="true">{initialsOf(s.displayName)}</span>
                  <div className="suggest__who">
                    <span className="roles__name">{s.displayName}</span>
                    <span className="roles__meta">
                      {[s.jobTitle, s.legalEntityName].filter(Boolean).join(' · ')}
                      {!s.signedIn && <span className="statuspill statuspill--pending">Not signed in</span>}
                    </span>
                    <span className="suggest__what">
                      <span className={`rolechip${s.orgUnitId || chosen ? ' chip--scoped' : ''}`}>
                        {chosen ? `Add ${s.roleName} for ${chosen.words}` : suggestionAction(s)}
                      </span>
                      {chosen && <span className="suggest__changed">Scope changed from {s.orgUnitName ?? 'the whole company'}</span>}
                      {scopable && (
                        <button type="button" className="btn btn--link" disabled={working} onClick={() => onScope!(s)}>
                          {chosen ? 'Edit scope' : 'Change scope'}
                        </button>
                      )}
                    </span>
                    {machine && (
                      <span className="suggest__machine">
                        <OneMarkIcon size={12} /> {machine}
                      </span>
                    )}
                    {plain && <span className="suggest__match">{plain}</span>}
                  </div>
                  <div className="suggest__acts">
                    <button type="button" className="btn btn--primary btn--xs" disabled={working} onClick={() => onAccept(s)}>
                      {busy === `accept:${token}` ? '…' : 'Accept'}
                    </button>
                    <button type="button" className="btn btn--quiet btn--xs" disabled={working} onClick={() => onDecline(s)}>
                      {busy === `decline:${token}` ? '…' : 'Decline'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--quiet btn--xs"
                      disabled={working}
                      title="Hide this for 30 days. Not a decline: nothing is recorded."
                      onClick={() => onLater(s)}
                    >
                      {busy === `later:${token}` ? '…' : 'Later'}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}

/**
 * Decline, with optional reasons and an optional note. The note is about a
 * person ("leaving in December"), so it is never sent to OneConetec and only
 * administrators who could grant to that person can read it.
 */
export function DeclineDialog({
  suggestion,
  many = null,
  onCancel,
  onDecline,
}: {
  suggestion: GrantSuggestion | null
  /** 159. Several at once: the reasons and the note go with every one. */
  many?: GrantSuggestion[] | null
  onCancel: () => void
  onDecline: (reasons: DeclineReason[], note: string) => void
}) {
  const [reasons, setReasons] = useState<DeclineReason[]>([])
  const [note, setNote] = useState('')
  useEffect(() => {
    setReasons([])
    setNote('')
  }, [suggestion?.personId, suggestion?.holderId, many?.length])

  const bulk = many !== null && many.length > 0
  const title = bulk
    ? `Decline ${many.length} suggestion${many.length === 1 ? '' : 's'}`
    : suggestion
      ? `Decline: ${suggestionAction(suggestion)} for ${suggestion.displayName}`
      : 'Decline'
  const bySort = bulk ? many.filter((m) => m.matchedOn === 'function').length : 0
  return (
    <Dialog open={bulk || suggestion !== null} title={title} onClose={onCancel}>
      <form
        className="suggest__decline"
        onSubmit={(e) => {
          e.preventDefault()
          onDecline(reasons, note)
        }}
      >
        <fieldset className="suggest__reasons">
          <legend>
            Why? <span className="admin__muted">Optional. Pick any that apply.</span>
          </legend>
          {DECLINE_REASONS.map((r) => (
            <label key={r.code} className="suggest__reason">
              <input
                type="checkbox"
                checked={reasons.includes(r.code)}
                onChange={(e) =>
                  setReasons((prev) => (e.target.checked ? [...prev, r.code] : prev.filter((x) => x !== r.code)))
                }
              />
              {r.label}
            </label>
          ))}
        </fieldset>
        {bulk && (
          <p className="admin__muted suggest__hint">
            The reasons and the note are kept with each of the {many.length}.
            {bySort > 0 && reasons.includes('wrong_function') &&
              ` For the ${bySort} that matched through a title’s sort, this also rejects that sort, so no typical holder matches on it again until HR sorts it.`}
          </p>
        )}
        {!bulk && suggestion?.matchedOn === 'function' && reasons.includes('wrong_function') && (
          <p className="admin__muted suggest__hint">
            This also rejects the sort of “{suggestion.jobTitle}” as {suggestion.functionLabel}, so no typical holder
            matches on it again until HR sorts it.
          </p>
        )}
        <label className="admin__field suggest__note">
          <span className="admin__searchlabel">
            Anything else <span className="admin__muted">Optional. Stays here; never sent to OneConetec.</span>
          </span>
          <textarea value={note} maxLength={1000} rows={3} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="admin__inviteactions">
          <button type="submit" className="btn btn--primary btn--sm">
            {bulk ? `Decline ${many.length}` : 'Decline'}
          </button>
          <button type="button" className="btn btn--quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * The answers, newest first. An audit trail, so it says who answered — and is
 * never totalled per person (B22). Changing your mind is a new answer: a
 * declined suggestion that still stands can be accepted instead.
 */
export function AnswerList({
  answers,
  busy,
  onAcceptInstead,
}: {
  answers: SuggestionAnswer[]
  busy: string | null
  onAcceptInstead: (a: SuggestionAnswer) => void
}) {
  return (
    <details className="people__misses suggest__answers">
      <summary>Answered recently ({answers.length})</summary>
      <ul>
        {answers.map((a) => (
          <li key={a.outcomeId}>
            <strong>{a.displayName}</strong> — {a.roleName}
            {a.orgUnitName ? ` for ${a.orgUnitName}` : ''} ({a.holderName}):{' '}
            {a.outcome === 'correct' ? 'accepted' : 'declined'}
            {a.decidedByName ? ` by ${a.decidedByName}` : ''}, {new Date(a.decidedAt).toLocaleDateString()}
            {a.reasonCodes.length > 0 && <> · {a.reasonCodes.map(reasonLabel).join(', ')}</>}
            {a.reasonText && <> · “{a.reasonText}”</>}
            {a.outcome === 'incorrect' && a.stillSuggested && a.currentEvidenceMd5 && (
              <>
                {' '}
                <button
                  type="button"
                  className="btn btn--link"
                  disabled={busy !== null}
                  onClick={() => onAcceptInstead(a)}
                >
                  Accept instead
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </details>
  )
}
